import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ShellMembership, ShellStatus } from '../../components/shell/useShellSession';
import { supabase } from '../supabase';

export interface OrgSwitcherInput {
  memberships: ReadonlyArray<ShellMembership>;
  status: ShellStatus;
  activeOrgId: string | null;
  /** Lower-cased email of the signed-in viewer, used to filter the
   *  pending-invitations PostgREST read (17.3b.1). When `null` the VM keeps
   *  `state.pendingInvitations = []` + `state.invitationsStatus = 'idle'`. */
  viewerEmail?: string | null;
}

/** Cross-tab broadcast message union — extended in 17.3b.1 to carry the
 *  invitation-accepted / invitation-declined kinds so other tabs' VMs can
 *  optimistically remove the row from their own `pendingInvitations` cache. */
export type OrgSwitcherBroadcast =
  | { kind: 'switched'; orgId: string; name: string }
  | { kind: 'invitation-accepted'; invitationId: string }
  | { kind: 'invitation-declined'; invitationId: string };

/** Side-effect handles wired by the consumer (typically `SignedInShell`).
 *  Kept off the input contract so the VM stays a thin React hook — the
 *  consumer owns router / BroadcastChannel wiring. */
export interface OrgSwitcherConfig {
  /** Flip the per-tab active org (header chrome + toast). Called synchronously
   *  on every `beginSwitch` before the refetch + navigate. */
  onActiveOrgChange?: (orgId: string, orgName: string) => void;
  /** Reload session bundle (identity / role / capabilities) for the new org.
   *  Wired to `useShellSession.refresh` in production. */
  refreshSession?: () => Promise<void> | void;
  /** Land the user on the post-switch surface. Wired to react-router's
   *  `navigate('/dashboard')` in production. */
  navigate?: (path: string) => void;
  /** Optional `BroadcastChannel('penrose-org-changed')` emit so other tabs'
   *  org-rename observers + the dashboard's pending-invitations card refresh.
   *  Per the 2026-05-13 per-tab decision, other tabs do NOT change their
   *  active org on `switched` — the broadcast is informational. */
  broadcast?: (message: OrgSwitcherBroadcast) => void;
  /** Subscribe to inbound cross-tab broadcasts. The VM removes matching rows
   *  from `state.pendingInvitations` when it receives an
   *  `invitation-accepted` / `invitation-declined` message. Returns an
   *  unsubscribe. */
  subscribeBroadcast?: (
    listener: (message: OrgSwitcherBroadcast) => void
  ) => () => void;
}

export type OrgSwitchPhase = 'idle' | 'switching';

export type InvitationsStatus = 'idle' | 'loading' | 'ready' | 'error';

/** 17.4a — Shape parked in `state.revocation` when the active org's membership
 *  disappears mid-session (admin-side revocation observed via the next session
 *  refetch, or surfaced eagerly via `markRevoked` when a capability fetch
 *  returns 403). `revokedOrgName` is the *last-known* name (read from the
 *  previous memberships snapshot before the membership row vanished) so the
 *  full-page error in 17.4b can render "Your access to <Acme Co> was revoked"
 *  without holding a separate lookup. */
export interface RevocationState {
  revoked: true;
  revokedOrgId: string;
  revokedOrgName: string;
}

/** 17.3b.1 — Row shape exposed by `state.pendingInvitations`. Sourced from
 *  the viewer's pending `invitations` rows via PostgREST embed joins on
 *  `organizations` (org name) + `profiles` (inviter display name). */
export interface PendingInvitation {
  id: string;
  token: string;
  org_id: string;
  org_name: string;
  inviter_name: string | null;
  expires_at: string;
}

export interface OrgSwitcherState {
  status: ShellStatus;
  activeOrgId: string | null;
  switchPhase: OrgSwitchPhase;
  pendingInvitations: ReadonlyArray<PendingInvitation>;
  invitationsStatus: InvitationsStatus;
  invitationsError: string | null;
  /** 17.4a — Populated when the active org's membership disappears mid-session
   *  (or when a consumer calls `markRevoked` after a 403). Null in the steady
   *  state. The 17.4b UI surface keys off this flag to swap the content slot
   *  for `<RevocationError>`. */
  revocation: RevocationState | null;
}

export interface OrgSwitcherDerived {
  /** Memberships sorted recently-active first, then alphabetical. */
  sorted: ReadonlyArray<ShellMembership>;
  /** The active membership (matched against `activeOrgId`) or null. */
  active: ShellMembership | null;
  /** Sorted memberships minus the active one (the "Switch to" rows). */
  others: ReadonlyArray<ShellMembership>;
  /** True when the user has 2+ memberships. */
  hasMultipleOrgs: boolean;
  /** Live `AbortSignal` for the in-flight switch. Broken on each new
   *  `beginSwitch` so consumers (document editor, task detail) can abort
   *  their in-flight RPCs when the active org flips mid-load. Always present;
   *  in the `idle` phase the signal corresponds to the most recent switch
   *  (already-resolved or never-fired) — checking `.aborted` is the contract. */
  currentSwitchSignal: AbortSignal;
  /** 17.4a — Last-known revoked org name when `state.revocation` is set;
   *  null otherwise. Header chrome reads this to show the prior org's name
   *  while the revocation surface is mounted. */
  revokedOrgName: string | null;
  /** 17.4a — Convenience boolean equivalent to `state.revocation !== null`.
   *  Lets header / shell consumers gate on a single field without poking into
   *  the revocation envelope. */
  isRevoked: boolean;
}

export interface OrgSwitcherActions {
  /** Run the switch workflow for `orgId`: aborts the previous switch's
   *  `AbortController`, flips `switchPhase` to `'switching'`, calls
   *  `onActiveOrgChange` (header + toast), emits the broadcast, awaits
   *  `refreshSession`, navigates to `/dashboard`, then flips `switchPhase`
   *  back to `'idle'`. No-op when `orgId` matches the current `activeOrgId`
   *  or when the org is not a known membership. */
  beginSwitch: (orgId: string, optimisticName?: string) => Promise<void>;
  /** 17.3b.1 — Accept a pending invitation by token. On success reads
   *  `new_org_id` from the RPC envelope, optimistically removes the row,
   *  emits an `invitation-accepted` broadcast, then delegates to
   *  `beginSwitch(new_org_id)` so the user lands in the just-joined org. */
  acceptInvitation: (token: string) => Promise<void>;
  /** 17.3b.1 — Decline a pending invitation by id. Optimistically removes
   *  the row + emits an `invitation-declined` broadcast, calls the
   *  `decline_invitation` RPC; reverts state + parks
   *  `state.invitationsError` on failure. */
  declineInvitation: (invitationId: string) => Promise<void>;
  /** 17.3b.1 — Test-only refetch (the VM auto-loads on viewerEmail change;
   *  exposed for the post-accept/post-decline refresh paths the UI surface
   *  in 17.3b.2 will wire). */
  refreshInvitations: () => Promise<void>;
  /** 17.4a — Surface a revocation eagerly without waiting for the next
   *  memberships refetch. Capability fetch wrappers / future error boundaries
   *  call this when a 403 is observed for `orgId` so the UI can flip to the
   *  full-page error immediately. Idempotent: re-fires for the same orgId
   *  refresh the parked `revokedOrgName` (useful when the consumer has a
   *  later read on the org name). */
  markRevoked: (orgId: string, orgName: string) => void;
  /** 17.4a — Clear the revocation flag. `beginSwitch` calls this on its
   *  success path when the switched-to org differs from the revoked org, so
   *  the next `/dashboard` render unmounts the revocation surface. Exposed
   *  for test seams + future error boundaries to reset manually. */
  clearRevocation: () => void;
}

export interface OrgSwitcherVM {
  state: OrgSwitcherState;
  derived: OrgSwitcherDerived;
  actions: OrgSwitcherActions;
}

/**
 * Order memberships recently-active first, then alphabetical by org name.
 *
 * - Rows with a non-null `last_active_at` precede rows without, sorted
 *   descending by `last_active_at` so the most recently active org is first.
 * - Rows with the same `last_active_at` (or both null) fall back to
 *   alphabetical by `org_name`.
 *
 * Follow-up (2026-05-15): the `memberships.last_active_at` column is not on
 * the schema yet — `useShellSession` selects it defensively and the field is
 * undefined for every row in production. The sort degrades to
 * alphabetical-only until the column lands; no consumer change is needed when
 * it does. The fallback intentionally drops the per-row `created_at` proxy
 * the brief mentioned so the never-active group's ordering stays stable as
 * memberships join over time.
 */
export function sortMembershipsRecentlyActive<
  T extends { last_active_at?: string | null; org_name: string }
>(rows: ReadonlyArray<T>): T[] {
  return [...rows].sort((a, b) => {
    const aActive = a.last_active_at ?? null;
    const bActive = b.last_active_at ?? null;
    if (aActive && bActive) {
      if (aActive !== bActive) return bActive.localeCompare(aActive);
    } else if (aActive) {
      return -1;
    } else if (bActive) {
      return 1;
    }
    return a.org_name.localeCompare(b.org_name);
  });
}

type InvitationsRowWire = {
  id: string;
  token: string;
  org_id: string;
  invited_by: string | null;
  expires_at: string;
  orgs: { name?: string } | { name?: string }[] | null;
};

function projectInvitationRow(
  row: InvitationsRowWire,
  inviterNameById: Readonly<Record<string, string>>
): PendingInvitation {
  const orgName = Array.isArray(row.orgs)
    ? row.orgs[0]?.name ?? '—'
    : row.orgs?.name ?? '—';
  const inviterName = row.invited_by
    ? inviterNameById[row.invited_by] ?? null
    : null;
  return {
    id: row.id,
    token: row.token,
    org_id: row.org_id,
    org_name: orgName,
    inviter_name: inviterName,
    expires_at: row.expires_at
  };
}

/**
 * View-model for the org switcher surface (identity-flyout "Switch to"
 * section + the future full-page revocation picker + the pending-invitations
 * card on /dashboard). Owns ordering of the user's memberships, the partition
 * into active/others, the switch workflow (17.2a), AND the pending-invitations
 * load + accept/decline action surface (17.3b.1).
 *
 * Separated from `useShellSession`, which remains the loader of profile + raw
 * memberships, so subsequent org-switcher turns (17.4 — revocation) can extend
 * this VM without touching the shell session.
 *
 * **Adaptation note (17.3b.1):** the brief asked for `supabase` to be
 * injected via config to keep the VM "pure"; in practice the existing
 * `useShellSession` and every other VM in the repo import the `supabase`
 * singleton directly, and Playwright specs intercept the underlying HTTP
 * traffic via `page.route` regardless of how the client is constructed.
 * Matching the existing pattern (direct import) keeps the VM consistent
 * with the rest of the codebase; the test seam is the wire, not the client
 * shape. If a future need arises to swap the client (e.g., a unit test that
 * doesn't drive the browser), the import can be lifted into a config field
 * without touching consumers.
 */
export function useOrgSwitcherVM(
  input: OrgSwitcherInput,
  config?: OrgSwitcherConfig
): OrgSwitcherVM {
  const { memberships, status, activeOrgId } = input;
  const viewerEmail = input.viewerEmail ?? null;

  const sorted = useMemo(
    () => sortMembershipsRecentlyActive(memberships),
    [memberships]
  );

  const active = useMemo(
    () => sorted.find((m) => m.org_id === activeOrgId) ?? null,
    [sorted, activeOrgId]
  );

  const others = useMemo(
    () => sorted.filter((m) => m.org_id !== activeOrgId),
    [sorted, activeOrgId]
  );

  const membershipById = useMemo(() => {
    const map = new Map<string, ShellMembership>();
    for (const m of memberships) map.set(m.org_id, m);
    return map;
  }, [memberships]);

  // Live mirror of the latest config so an in-flight switch picks up the
  // current callbacks (avoids capturing stale refs in the async callback).
  const configRef = useRef<OrgSwitcherConfig | undefined>(config);
  configRef.current = config;

  const [switchPhase, setSwitchPhase] = useState<OrgSwitchPhase>('idle');
  const switchAbortRef = useRef<AbortController>(new AbortController());

  const [pendingInvitations, setPendingInvitations] = useState<
    ReadonlyArray<PendingInvitation>
  >([]);
  const [invitationsStatus, setInvitationsStatus] =
    useState<InvitationsStatus>('idle');
  const [invitationsError, setInvitationsError] = useState<string | null>(null);

  // 17.4a — revocation detection.
  const [revocation, setRevocation] = useState<RevocationState | null>(null);
  const revocationRef = useRef<RevocationState | null>(revocation);
  revocationRef.current = revocation;
  // The detector needs the previous-render memberships snapshot to look up the
  // revoked org's name *after* it has vanished from the current array. React
  // doesn't expose previous-props through hook semantics so we mirror the
  // pattern used by `loadEpochRef` above and stash via a ref. Initialised to
  // the first render's value so the first effect run is a no-op.
  const prevMembershipsRef =
    useRef<ReadonlyArray<ShellMembership>>(memberships);

  // Live mirrors so callbacks (accept/decline/test-seam getState) read the
  // freshest values without re-binding on every render.
  const pendingInvitationsRef =
    useRef<ReadonlyArray<PendingInvitation>>(pendingInvitations);
  pendingInvitationsRef.current = pendingInvitations;
  const invitationsStatusRef = useRef<InvitationsStatus>(invitationsStatus);
  invitationsStatusRef.current = invitationsStatus;
  const invitationsErrorRef = useRef<string | null>(invitationsError);
  invitationsErrorRef.current = invitationsError;

  // Epoch counter so a slow in-flight read can't clobber a newer one's result.
  const loadEpochRef = useRef(0);

  // Tear down on unmount — abort the currently-tracked controller so any
  // consumer holding the signal can stop its work.
  useEffect(() => {
    const ctrl = switchAbortRef.current;
    return () => {
      ctrl.abort();
    };
  }, []);

  // 17.4a — revocation detector. Runs after every memberships / activeOrgId
  // change. When (a) `state.revocation` is null AND (b) the active org was in
  // the previous memberships snapshot AND (c) it is NOT in the current
  // memberships array, park the revocation envelope using the previous
  // snapshot's org_name as the last-known display name. After the detector
  // run, advance `prevMembershipsRef` so the next render sees the post-revoke
  // array as the new baseline.
  useEffect(() => {
    if (revocationRef.current === null && activeOrgId !== null) {
      const prev = prevMembershipsRef.current;
      const lostMember = prev.find((m) => m.org_id === activeOrgId);
      const stillMember = memberships.some(
        (m) => m.org_id === activeOrgId
      );
      if (lostMember && !stillMember) {
        setRevocation({
          revoked: true,
          revokedOrgId: activeOrgId,
          revokedOrgName: lostMember.org_name
        });
      }
    }
    prevMembershipsRef.current = memberships;
  }, [memberships, activeOrgId]);

  const loadPendingInvitations = useCallback(async (email: string | null) => {
    if (!email) {
      setPendingInvitations([]);
      setInvitationsStatus('idle');
      setInvitationsError(null);
      return;
    }
    const epoch = ++loadEpochRef.current;
    setInvitationsStatus('loading');
    setInvitationsError(null);
    try {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('invitations')
        .select(
          'id, token, org_id, invited_by, expires_at, orgs:org_id(name)'
        )
        .eq('email', email.toLowerCase())
        .is('accepted_at', null)
        .is('declined_at', null)
        .is('revoked_at', null)
        .is('deleted_at', null)
        .gt('expires_at', nowIso)
        .order('expires_at', { ascending: true });
      if (epoch !== loadEpochRef.current) return;
      if (error) {
        setInvitationsStatus('error');
        setInvitationsError(error.message);
        return;
      }
      const rows = (data ?? []) as ReadonlyArray<InvitationsRowWire>;
      // The invitations.invited_by FK was relaxed (migration
      // 20260514002422_relax_created_by_invited_by_fks), so PostgREST cannot
      // embed inviter.display_name through that column. Look up display names
      // for the distinct inviter ids in a follow-up profiles read.
      const inviterIds = Array.from(
        new Set(
          rows
            .map((r) => r.invited_by)
            .filter((id): id is string => typeof id === 'string' && id.length > 0)
        )
      );
      const inviterNameById: Record<string, string> = {};
      if (inviterIds.length > 0) {
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, display_name, username')
          .in('id', inviterIds);
        if (epoch !== loadEpochRef.current) return;
        for (const p of (profileRows ?? []) as ReadonlyArray<{
          id: string;
          display_name: string | null;
          username: string | null;
        }>) {
          inviterNameById[p.id] = p.display_name || p.username || 'someone';
        }
      }
      setPendingInvitations(
        rows.map((r) => projectInvitationRow(r, inviterNameById))
      );
      setInvitationsStatus('ready');
      setInvitationsError(null);
    } catch (err) {
      if (epoch !== loadEpochRef.current) return;
      setInvitationsStatus('error');
      setInvitationsError(
        err instanceof Error ? err.message : 'Failed to load invitations'
      );
    }
  }, []);

  useEffect(() => {
    void loadPendingInvitations(viewerEmail);
  }, [viewerEmail, loadPendingInvitations]);

  const beginSwitch = useCallback(
    async (orgId: string, optimisticName?: string) => {
      // Resolve the org name from the local memberships map; the
      // post-accept-invitation path passes `optimisticName` because the
      // newly-minted membership isn't in the local map until
      // `refreshSession` lands.
      const member = membershipById.get(orgId);
      const orgName = member?.org_name ?? optimisticName ?? null;
      if (!orgName) return;
      if (orgId === activeOrgId) return;

      // Abort the previous switch's controller — consumers picking up
      // `derived.currentSwitchSignal` see `.aborted === true` immediately.
      switchAbortRef.current.abort();
      const ctrl = new AbortController();
      switchAbortRef.current = ctrl;

      setSwitchPhase('switching');

      try {
        configRef.current?.onActiveOrgChange?.(orgId, orgName);
      } catch {
        /* consumer threw — don't block the switch */
      }

      try {
        configRef.current?.broadcast?.({
          kind: 'switched',
          orgId,
          name: orgName
        });
      } catch {
        /* BroadcastChannel unavailable */
      }

      try {
        await configRef.current?.refreshSession?.();
      } catch {
        /* dashboard's own VM will surface its load error */
      }

      if (ctrl.signal.aborted) return;

      try {
        configRef.current?.navigate?.('/dashboard');
      } catch {
        /* navigate threw — leave the user on the current route */
      }

      // 17.4a — when the user successfully switches OUT of a revoked org
      // context (revoked org `R`, picker click resolves to org `T !== R`),
      // clear the revocation flag so the next render unmounts the
      // `<RevocationError>` surface and the new org's `/dashboard` renders.
      const current = revocationRef.current;
      if (current && current.revokedOrgId !== orgId) {
        setRevocation(null);
      }

      setSwitchPhase('idle');
    },
    [membershipById, activeOrgId]
  );

  const markRevoked = useCallback((orgId: string, orgName: string) => {
    setRevocation({
      revoked: true,
      revokedOrgId: orgId,
      revokedOrgName: orgName
    });
  }, []);

  const clearRevocation = useCallback(() => {
    setRevocation(null);
  }, []);

  // Stable ref so `acceptInvitation` (declared below) can call the latest
  // `beginSwitch` without rebinding on every render.
  const beginSwitchRef = useRef(beginSwitch);
  beginSwitchRef.current = beginSwitch;

  const acceptInvitation = useCallback(async (token: string) => {
    setInvitationsError(null);
    try {
      const { data, error } = await supabase.rpc('accept_invitation', {
        p_token: token
      });
      if (error) {
        setInvitationsError(error.message);
        return;
      }
      // The live RPC (`returns table(org_id, org_name)`) surfaces an array
      // of rows; CLI specs mock a `{accepted, new_org_id}` jsonb envelope.
      // Normalize both shapes — read `new_org_id` from the envelope when
      // present, otherwise fall back to the first row's `org_id` (matches
      // `src/pages/Invite.tsx`'s consumer pattern).
      const arrayRow =
        Array.isArray(data) && data[0]
          ? (data[0] as { org_id?: string })
          : null;
      const envelope = (Array.isArray(data) ? {} : data ?? {}) as {
        accepted?: boolean;
        new_org_id?: string;
        reason?: string;
      };
      const newOrgId = envelope.new_org_id ?? arrayRow?.org_id ?? null;
      if (envelope.accepted === false) {
        setInvitationsError(envelope.reason ?? 'invitation_not_accepted');
        return;
      }
      // Optimistic remove of the matching row + broadcast.
      const before = pendingInvitationsRef.current;
      const target = before.find((p) => p.token === token);
      if (target) {
        setPendingInvitations(before.filter((p) => p.token !== token));
        try {
          configRef.current?.broadcast?.({
            kind: 'invitation-accepted',
            invitationId: target.id
          });
        } catch {
          /* BroadcastChannel unavailable */
        }
      }
      if (newOrgId) {
        await beginSwitchRef.current(newOrgId, target?.org_name);
      }
    } catch (err) {
      setInvitationsError(
        err instanceof Error ? err.message : 'Failed to accept invitation'
      );
    }
  }, []);

  const declineInvitation = useCallback(async (invitationId: string) => {
    const before = pendingInvitationsRef.current;
    const target = before.find((p) => p.id === invitationId);
    if (!target) return;
    // Optimistic remove + broadcast.
    setPendingInvitations(before.filter((p) => p.id !== invitationId));
    setInvitationsError(null);
    try {
      configRef.current?.broadcast?.({
        kind: 'invitation-declined',
        invitationId
      });
    } catch {
      /* BroadcastChannel unavailable */
    }
    try {
      const { data, error } = await supabase.rpc('decline_invitation', {
        p_invitation_id: invitationId
      });
      if (error) {
        setPendingInvitations(before);
        setInvitationsError(error.message);
        return;
      }
      const envelope = (data ?? {}) as { declined?: boolean; reason?: string };
      if (envelope.declined === false) {
        // RPC succeeded but the server-side gate rejected — revert + park.
        setPendingInvitations(before);
        setInvitationsError(envelope.reason ?? 'invitation_not_declined');
      }
    } catch (err) {
      setPendingInvitations(before);
      setInvitationsError(
        err instanceof Error ? err.message : 'Failed to decline invitation'
      );
    }
  }, []);

  const refreshInvitations = useCallback(async () => {
    await loadPendingInvitations(viewerEmail);
  }, [loadPendingInvitations, viewerEmail]);

  // Inbound broadcast handler — other tabs that accept/decline an invitation
  // post a message on the same channel; mirror the removal here so the row
  // disappears in every tab simultaneously.
  useEffect(() => {
    const subscribe = configRef.current?.subscribeBroadcast;
    if (!subscribe) return;
    const unsubscribe = subscribe((message) => {
      if (
        message.kind === 'invitation-accepted' ||
        message.kind === 'invitation-declined'
      ) {
        const id = message.invitationId;
        setPendingInvitations((prev) => prev.filter((p) => p.id !== id));
      }
    });
    return unsubscribe;
  }, []);

  // Test-only window handle so Playwright specs can drive the VM's actions
  // + read state without mounting a full UI surface. Mirrors the
  // `window.__penroseUnsavedDrafts` seam shipped in 17.2b. Extended in 17.4a
  // with the revocation getters/actions + a `triggerSessionRefresh()` that
  // re-invokes the wired `refreshSession` callback so specs can drive the
  // memberships-refetch detector path without mocking a click flow.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handle = {
      getState: () => ({
        pendingInvitations: pendingInvitationsRef.current,
        invitationsStatus: invitationsStatusRef.current,
        invitationsError: invitationsErrorRef.current,
        revocation: revocationRef.current
      }),
      acceptInvitation,
      declineInvitation,
      refreshInvitations,
      getRevocation: () => revocationRef.current,
      markRevoked,
      clearRevocation,
      triggerSessionRefresh: async () => {
        await configRef.current?.refreshSession?.();
      }
    };
    (window as unknown as {
      __penroseOrgSwitcher?: typeof handle;
    }).__penroseOrgSwitcher = handle;
    return () => {
      delete (window as unknown as {
        __penroseOrgSwitcher?: typeof handle;
      }).__penroseOrgSwitcher;
    };
  }, [
    acceptInvitation,
    declineInvitation,
    refreshInvitations,
    markRevoked,
    clearRevocation
  ]);

  return {
    state: {
      status,
      activeOrgId,
      switchPhase,
      pendingInvitations,
      invitationsStatus,
      invitationsError,
      revocation
    },
    derived: {
      sorted,
      active,
      others,
      hasMultipleOrgs: memberships.length > 1,
      currentSwitchSignal: switchAbortRef.current.signal,
      revokedOrgName: revocation?.revokedOrgName ?? null,
      isRevoked: revocation !== null
    },
    actions: {
      beginSwitch,
      acceptInvitation,
      declineInvitation,
      refreshInvitations,
      markRevoked,
      clearRevocation
    }
  };
}
